import React, { useState, useEffect } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import Step10InventoryModal from '../components/Step10InventoryModal';
import { progressService } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';

interface Step10Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step10: React.FC<Step10Props> = ({ user, onLogin, onLogout }) => {
  const { showAlert } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [entryCount, setEntryCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Ten",
      description: "Discover how daily personal inventory and prompt admission sustain clarity and connection in my recovery",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Ten",
      description: "Read practical guidance on how to work Step Ten effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Take Step Ten: Continue to take personal inventory and promptly admit when wrong",
      description: "Practice ongoing self-examination and admit mistakes promptly",
      completed: false,
      hasButton: true,
      buttonText: "Practice Inventory",
      buttonAction: () => setIsInventoryModalOpen(true)
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Ten', path: '/saa/12-steps/step-10', active: true }
  ];

  // Check if an entry is complete (has date and note text)
  const isEntryComplete = (entry: any): boolean => {
    if (!entry || typeof entry !== 'object') return false;
    
    const date = entry.date && typeof entry.date === 'string' ? entry.date.trim() : '';
    const note = entry.note && typeof entry.note === 'string' ? entry.note.trim() : '';
    
    return !!(date.length > 0 && note.length > 0);
  };

  // Load entry count and completion status
  const loadEntryCount = async () => {
    if (!user) {
      setEntryCount(0);
      setCompletedCount(0);
      return;
    }
    
    try {
      const items = await InventoryService.getInventory('step10-inventories', 'default');
      // Filter and map items the same way as Step10InventoryModal does
      const inventoryEntries = items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          date: item.date || '',
          note: item.note || ''
        }));
      setEntryCount(inventoryEntries.length);
      
      // Count completed entries using the same logic
      const completed = inventoryEntries.filter(item => isEntryComplete(item)).length;
      setCompletedCount(completed);
    } catch (error) {
      // If no entries exist yet, counts are 0
      setEntryCount(0);
      setCompletedCount(0);
    }
  };

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Load entry count
      await loadEntryCount();
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 10).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          // Merge saved actions with original actions to restore missing properties
          const mergedActions = originalActions.map(originalAction => {
            const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
            if (savedAction) {
              return {
                ...originalAction,
                completed: savedAction.completed,
                description: savedAction.description || originalAction.description
              };
            }
            return originalAction;
          });
          setActions(mergedActions);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err) {
      console.error('Error initializing progress:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // Check if trying to complete action 3
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Validate that we have 5 complete entries
      if (completedCount < 5) {
        showAlert({
          message: `To complete this action, you need to complete 5 inventory entries. You currently have ${completedCount} of 5 complete.`,
          variant: 'warning'
        });
        // Don't update actions - keep action 3 unchecked
        return;
      }
    }
    
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 10,
        step_title: 'Step Ten',
        completed: allCompleted,
        data: {
          actions: updatedActions
        }
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Ten - ${completedCount} of 3 actions completed`,
        progress_milestone: allCompleted ? 'step10_completed' : 'step10_in_progress'
      });
      
      clearError();
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Ten</h1>
            {stepData && (
              <HistoryButton
                stepNumber={10}
                stepTitle="Step Ten"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Continued to take personal inventory and when we were wrong promptly admitted it.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {!isInitializing && !errorMessage && user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Ten</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              stepNumber={10}
              renderActionContent={(action, index) => {
                if (action.id === 3) {
                  const remainingDays = 5 - completedCount;
                  return (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      color: '#6b7280', 
                      fontSize: '0.875rem',
                      paddingLeft: '56px' // Align with button: actionNumber (32px) + margin-right (0.75rem ≈ 12px) + gap (0.75rem ≈ 12px) = 56px
                    }}>
                      {entryCount === 0 ? (
                        <p style={{ margin: 0, fontStyle: 'italic' }}>
                          Practice taking a nightly inventory for 5 different days (they don't have to be consecutive). You can add entries for as many days as you'd like, but you only need to complete 5 to finish this action. Click "Practice Inventory" to get started.
                        </p>
                      ) : completedCount >= 5 ? (
                        <span>
                          {completedCount} {completedCount === 1 ? 'day complete' : 'days complete'}. You have completed this action. {entryCount > completedCount && `You have ${entryCount - completedCount} additional ${entryCount - completedCount === 1 ? 'entry' : 'entries'} that ${entryCount - completedCount === 1 ? 'is' : 'are'} not yet complete.`}
                        </span>
                      ) : (
                        <span>
                          {completedCount} {completedCount === 1 ? 'day complete' : 'days complete'}. Practice for {remainingDays} more {remainingDays === 1 ? 'day' : 'days'} to complete this action. {entryCount > completedCount && `You have ${entryCount - completedCount} additional ${entryCount - completedCount === 1 ? 'entry' : 'entries'} that ${entryCount - completedCount === 1 ? 'is' : 'are'} not yet complete.`}
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Ten and can move on to Step Eleven.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                      {'<<'} Step Nine
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                      Step Eleven {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                    {'<<'} Step Nine
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                    Step Eleven {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-ten start>>"
        endTag="<<step-ten end>>"
      />

      {/* ContentReader for How to Work Step Ten */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_10_Guidance.md"
        title="Guidance on working Step Ten"
        startTag="<<working-step-ten start>>"
        endTag="<<working-step-ten end>>"
      />

      {/* Step 10 Inventory Modal */}
      <Step10InventoryModal
        isOpen={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        user={user}
        onListUpdate={loadEntryCount}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={10}
        stepTitle="Step Ten"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />

    </div>
  );
};

export default Step10; 